import React, { useState } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { TextInput } from '@rentspree/component-2023.components.atoms.text-input';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

export const stepTitle = 'Set your price and terms';
export const stepDescription =
  'Set the monthly rent and how long the lease will be.';

const PriceAndTerms: React.FC = () => {
  const [rent, setRent] = useState('2,500');
  const [selectedTerm, setSelectedTerm] = useState<string>('1-year');
  const [additionalTerms, setAdditionalTerms] = useState({
    pets: false,
    utilities: false,
    furnished: false,
  });

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdditionalTerms({
      ...additionalTerms,
      [event.target.name]: event.target.checked,
    });
  };

  const leaseTerms = [
    {
      id: 'month-to-month',
      label: 'Month-to-month',
      icon: <CalendarTodayIcon sx={{ fontSize: 20, color: '#666' }} />,
    },
    {
      id: '1-year',
      label: '1 year',
      icon: <EventIcon sx={{ fontSize: 20, color: '#666' }} />,
    },
    {
      id: 'other',
      label: 'Other',
      icon: <MoreHorizIcon sx={{ fontSize: 20, color: '#666' }} />,
    },
  ];

  return (
    <Box
      sx={{
        maxWidth: 600,
        margin: '0 auto',
        padding: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='subtitle1'
          sx={{
            fontWeight: 500,
            mb: 1,
            color: '#333',
          }}
        >
          Monthly Rent Amount
        </Typography>
        <TextInput
          value={rent}
          onChange={e => setRent(e.target.value)}
          startAdornment={
            <Typography
              sx={{
                color: '#999',
                ml: 1,
                fontSize: '1.25rem',
                fontWeight: 500,
              }}
            >
              $
            </Typography>
          }
          fullWidth
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '1.25rem',
              fontWeight: 500,
              color: '#333',
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography
          variant='subtitle1'
          sx={{
            fontWeight: 500,
            mb: 2,
            color: '#333',
          }}
        >
          Lease Term
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {leaseTerms.map(term => (
            <Box
              key={term.id}
              onClick={() => setSelectedTerm(term.id)}
              sx={{
                position: 'relative',
                flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 8px)' },
                minWidth: 120,
                height: 60,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                border:
                  selectedTerm === term.id
                    ? '2px solid #1976d2'
                    : '1px solid #e0e0e0',
                borderRadius: 2,
                backgroundColor: selectedTerm === term.id ? '#f3f8ff' : 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: selectedTerm === term.id ? '#1976d2' : '#bbb',
                  backgroundColor:
                    selectedTerm === term.id ? '#f3f8ff' : '#f9f9f9',
                },
              }}
            >
              {term.icon}
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 500,
                  color: selectedTerm === term.id ? '#1976d2' : '#333',
                }}
              >
                {term.label}
              </Typography>

              {/* Selected indicator */}
              {selectedTerm === term.id && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography
          variant='subtitle1'
          sx={{
            fontWeight: 500,
            mb: 2,
            color: '#333',
          }}
        >
          Additional Terms (Optional)
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={additionalTerms.pets}
                onChange={handleCheckboxChange}
                name='pets'
                sx={{ color: '#666' }}
              />
            }
            label={
              <Typography variant='body2' sx={{ color: '#333' }}>
                Pets allowed
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={additionalTerms.utilities}
                onChange={handleCheckboxChange}
                name='utilities'
                sx={{ color: '#666' }}
              />
            }
            label={
              <Typography variant='body2' sx={{ color: '#333' }}>
                Utilities included
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={additionalTerms.furnished}
                onChange={handleCheckboxChange}
                name='furnished'
                sx={{ color: '#666' }}
              />
            }
            label={
              <Typography variant='body2' sx={{ color: '#333' }}>
                Furnished
              </Typography>
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PriceAndTerms;
